import { memo } from "react";
import { selectProductPageAmount, selectVisibleTabularViewColumns } from "../../Search/redux/searchSelectors";
import { useDispatch, useSelector } from "react-redux";
import { changePage, changeSort } from "../../Search/redux/actions";
import { ReduxProductTabularView } from "~/Search/components/ProductResults/views/TabularView/ReduxProductTabularView";
import Pagination from "../../Search/components/common/Pagination";
import { type RootState } from "../../redux/store";
import { ReduxImperialToggle } from "~/ImperialMetricData/components/ImperialToggle/ImperialToggle";
import { saActiveTab } from "~/BearingSelectionAssistant/redux/selector";
import { SaTabs } from "~/BearingSelectionAssistant/redux/types";
import { useTranslation } from "react-i18next";
import { ResetSortBtn } from "~/BearingSelectionAssistant/components/Buttons/ResetSortBtn";

import classes from "./SATabularView.module.less";
import { OpenAbbreviationsBtn } from "~/BearingSelectionAssistant/components/AbbreviationListModal/AbbreviationListModal";
import { BSA_ATTRIBUTES } from "~/BearingSelectionAssistant/components/AbbreviationListModal/constants";
import { Alert } from "~/common/m3/Alert/Alert";

interface Props {
  totalNumberOfResults: number;
  isDesktop?: boolean;
}

function BSAResults() {
  const dispatch = useDispatch();
  const pageAmount = useSelector(selectProductPageAmount);
  const currentPage = useSelector(selectCurrentPage);
  const onPageChanged = (newPageNumber: number) => dispatch(changePage(newPageNumber));

  return (
    <>
      <ReduxProductTabularView maxColumnAmount={99} isSA={true} />
      {pageAmount > 1 && (
        <Pagination
          classes={{ root: "scha__sa__pagination" }}
          pageAmount={pageAmount}
          currentPage={currentPage}
          onPageChange={onPageChanged}
          goToIdAfterChange={"#results"}
          renderHrefs={true}
        />
      )}
    </>
  );
}

export const SATabularView = memo(({ totalNumberOfResults, isDesktop = false }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const config = useSelector(selectVisibleTabularViewColumns);
  const activeTab = useSelector(saActiveTab);

  if (!config) {
    return null;
  }

  return (
    <div className={"d-flex flex-direction-column"}>
      <h2 data-test-id={"selectionassistant-resultstitle"} className="scha__sa__results__title">
        {t("page.selectionAssistant.productsHeader", {
          count: totalNumberOfResults,
        })}
      </h2>
      {isDesktop && activeTab !== SaTabs.DIMENSIONS && <ReduxImperialToggle className={"mb-30"} page={"SA"} />}

      <div className={classes.buttonList}>
        <ResetSortBtn onClick={() => dispatch(changeSort("name"))} />

        {!isDesktop && <OpenAbbreviationsBtn attributes={BSA_ATTRIBUTES} />}
      </div>

      <BSAResults key={config.length} />

      <Alert severity={"info"} className={"mt-2"}>
        {t("page.selectionAssistant.units.blueMessage")}
      </Alert>
    </div>
  );
});

const selectCurrentPage = (state: RootState) => state.srp.currentPage;
